import { useState, type FormEvent } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/stores/toastStore';
import type { Employee } from '@/types';
import { formatNumber } from '@/utils/currency';
import { paieApi } from '@/api/paie';
import { SoldeToutCompteEmploye } from './SoldeToutCompteEmploye';

/**
 * PAIE-2 — onglet Paie de la fiche salarié (DAF et PDG seulement) : dossier, virement, éléments de paie mensuels.
 * Un élément s'applique sur une période ; on le termine (date de fin) au lieu de le modifier : le passé reste intact.
 */
const SITUATION: Record<string, string> = { celibataire: 'Célibataire', marie: 'Marié(e)', divorce: 'Divorcé(e)', veuf: 'Veuf / veuve' };
const dateFr = (iso: string | null | undefined) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('fr-FR', { timeZone: 'UTC' }) : '—');
const aujourdhui = () => new Date().toISOString().slice(0, 10);

export const PaieEmploye = ({ employee }: { employee: Employee }) => {
  const qc = useQueryClient();
  const cle = ['paie', 'elements', employee.id];
  const elements = useQuery({ queryKey: cle, queryFn: () => paieApi.elements(employee.id) });
  const rubriques = useQuery({ queryKey: ['paie', 'rubriques'], queryFn: paieApi.rubriques });
  const saisissables = (rubriques.data ?? []).filter((r) => r.active && r.calcul === 'saisie');
  const [f, setF] = useState({ rubriqueId: '', montant: '', du: aujourdhui() });

  const ajouter = useMutation({
    mutationFn: () => paieApi.creerElement({ employeeId: employee.id, rubriqueId: f.rubriqueId, montantCents: Math.round(Number(f.montant) * 100), du: f.du }),
    onSuccess: () => { toast.success('Élément de paie ajouté'); setF({ rubriqueId: '', montant: '', du: aujourdhui() }); void qc.invalidateQueries({ queryKey: cle }); },
  });
  const terminer = useMutation({
    mutationFn: ({ id, au }: { id: string; au: string }) => paieApi.terminerElement(id, au),
    onSuccess: () => { toast.success('Élément terminé'); void qc.invalidateQueries({ queryKey: cle }); },
  });
  const demanderFin = async (id: string) => {
    const au = await demander.texte({ titre: 'Terminer cet élément', libelle: 'Dernier jour d’application (AAAA-MM-JJ)', defaut: aujourdhui(), confirmer: 'Terminer' });
    if (au && /^\d{4}-\d{2}-\d{2}$/.test(au)) terminer.mutate({ id, au });
  };
  const soumettre = (e: FormEvent) => { e.preventDefault(); if (f.rubriqueId && Number(f.montant) >= 0 && f.montant !== '') ajouter.mutate(); };
  const e = employee;

  return (
    <>
      <div className="ds-dp-section">
        <div className="ds-dp-section-title">Dossier de paie</div>
        <div className="ds-dp-grid">
          <div className="ds-dp-item"><label>Situation</label><span>{e.situationFamiliale ? SITUATION[e.situationFamiliale] : '—'}</span></div>
          <div className="ds-dp-item"><label>Enfants à charge</label><span>{e.enfantsACharge ?? '—'}</span></div>
          <div className="ds-dp-item"><label>Catégorie</label><span>{e.categorieConvention ?? '—'}</span></div>
          <div className="ds-dp-item"><label>Ancienneté depuis</label><span>{dateFr(e.dateAnciennete ?? e.hireDate)}</span></div>
          <div className="ds-dp-item full"><label>Virement</label><span>{e.compteNumero ? `${e.banqueNom ?? ''} · ${e.compteIntitule ?? ''} · ${e.compteNumero}` : 'Coordonnées à saisir (Modifier la fiche)'}</span></div>
        </div>
      </div>

      <div className="ds-dp-section">
        <div className="ds-dp-section-title">Éléments de paie (par mois)</div>
        {elements.isLoading ? <div style={{ fontSize: 12 }}>Chargement…</div> : (elements.data?.length ?? 0) === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--tx-3)', marginBottom: 8 }}>Aucun élément. Commencez par le salaire de base.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: '0 0 8px', padding: 0, display: 'grid', gap: 6 }}>
            {elements.data!.map((el) => {
              const fini = !!el.au && el.au.slice(0, 10) < aujourdhui();
              return (
                <li key={el.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, opacity: fini ? 0.55 : 1 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{el.rubrique.libelle} · {formatNumber(Number(el.montantCents) / 100)} F</div>
                    <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>du {dateFr(el.du)}{el.au ? ` au ${dateFr(el.au)}` : ' — en cours'}</div>
                  </div>
                  {!el.au && <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => demanderFin(el.id)}>Terminer</button>}
                </li>
              );
            })}
          </ul>
        )}
        <form onSubmit={soumettre} style={{ display: 'grid', gap: 6 }}>
          <select className="ds-fi" value={f.rubriqueId} onChange={(ev) => setF({ ...f, rubriqueId: ev.target.value })} required>
            <option value="">— rubrique —</option>
            {saisissables.map((r) => <option key={r.id} value={r.id}>{r.libelle}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="ds-fi" inputMode="numeric" placeholder="Montant mensuel (F)" value={f.montant} onChange={(ev) => setF({ ...f, montant: ev.target.value.replace(/[^\d]/g, '') })} required />
            <input className="ds-fi" type="date" value={f.du} onChange={(ev) => setF({ ...f, du: ev.target.value })} required title="À partir du" />
          </div>
          <button className="ds-btn ds-btn-p ds-btn-sm" type="submit" disabled={ajouter.isPending} style={{ justifyContent: 'center' }}><Plus size={13} /> Ajouter</button>
          <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>Ancienneté, heures supplémentaires et absences sont calculées automatiquement (pas de saisie).</div>
        </form>
      </div>

      <SoldeToutCompteEmploye employee={employee} />
    </>
  );
};
